import React from 'react';
import _ from 'lodash';
import {
  Button, Modal, Collection, AlertBar, Link,
} from '@janus.team/janus-particles';

import { Loading } from 'components/loading/index';
import { proxyFetch } from '../../fetch';

const UnlinkWarnings = ({ warnings }) => {
  const knownErrorsMap = {
    UnlinkBillingAccountWarning: {
      reason: 'Failed to unlink project from billing account',
      customerAction: 'A GCP customer principal with billing/admin role must remove project from billing account.',
    },
    RevokeProjectGrantsWarning: {
      reason: 'Failed to remove Racker principals from project',
      customerAction: 'Customer must remove racker principals from project\'s policy',
    },
    RevokeProjectServiceAccountWarning: {
      reason: 'Failed to remove Rackspace service accounts from project',
      customerAction: 'Customer must remove service account principals from project\'s policy',
    },
  };
  return (
    <>
      <AlertBar level="warning">Unlink partially completed. Customer must complete following actions:</AlertBar>
      <br />
      <ul>
        {
          warnings.map(({ code, message }) => {
            const warning = knownErrorsMap[code];
            return (
              <li key={code}>
                <strong>{warning?.reason || `Unexpected error: ${code}`}</strong>
                <br />
                {warning?.customerAction || message}
              </li>
            );
          })
        }
      </ul>
    </>
  );
};

const ModalBody = ({ isInProgress, response, isSuccessfullyProvisioned }) => {
  if (isInProgress) {
    return <Loading centered />;
  }

  if (response?.error) {
    return (
      <AlertBar level="error">
        <strong>
          An error occurred while trying to unlink:
          {response.error}
        </strong>
        <br />
        Please try again later or create a ticket in the &quot;Janus Engineering&quot; Encore queue if issue persist.
      </AlertBar>
    );
  }

  if (response?.warnings && response.warnings.length > 0) {
    return <UnlinkWarnings warnings={response.warnings} />;
  }

  if (response) {
    return (
      <AlertBar level="success">
        Project successfully unlinked.
      </AlertBar>
    );
  }

  return (
    <>
      <strong>Note, this irreversible action will result in the following:</strong>
      <ol>
        <li>Janus and Rackers will lose access to the GCP Project.</li>
        <li>All service accounts created by Rackspace will be removed.</li>
        <li>
          The GCP project will be unlinked from the Rackspace-provided sub-billing account.
          All resources inside the GCP project may be deleted unless the customer links the project to another billing account.
        </li>
      </ol>
      {
        !isSuccessfullyProvisioned
        && (
          <AlertBar level="warning">
            Our service account may be missing permissions on this project and customer may have to complete these actions post unlink:
            <ul>
              <li>Unlink project from Rackspace Billing Account.</li>
              <li>Remove Racker and service accounts principals from project policy.</li>
            </ul>
            For a fully automated unlink, customer will have to
            <Link target="#" href="https://one.rackspace.com/pages/viewpage.action?spaceKey=MGCP&title=Service+Account+Permissions"> restore permissions for our service account prior unlinking.</Link>
          </AlertBar>
        )
      }
    </>
  );
};

const UnlinkProjectModal = ({
  onClose, projectId, onSuccess, isSuccessfullyProvisioned,
}) => {
  const [isInProgress, setInProgress] = React.useState(false);
  const [response, setResponse] = React.useState(null);

  const unlinkHandler = () => {
    setInProgress(true);
    const url = `${process.env.REACT_APP_GCP_API}/projects/${projectId}/linkedState`;
    return proxyFetch(url, {
      method: 'PUT',
      body: { linkedState: 'UNLINKED' },
    }, { useJanusLogin: true })
      .then((response) => response.json())
      .then((response) => {
        setResponse(response);
        return onSuccess();
      })
      .catch((error) => setResponse({ error: error.message }))
      .finally(() => setInProgress(false));
  };
  const onCloseHandler = () => {
    if (!isInProgress) {
      onClose();
    }
  };

  return (
    <>
      <Modal className="unlink-project-modal" data-testid="unlink-project-modal" size="medium" onClose={onCloseHandler}>
        <Modal.Header>
          <h3 className="hx particles">
            Unlink
            <strong>{` ${projectId}`}</strong>
          </h3>
        </Modal.Header>
        <Modal.Body>
          <ModalBody isInProgress={isInProgress} isSuccessfullyProvisioned={isSuccessfullyProvisioned} response={response} />
        </Modal.Body>
        <Modal.Footer>
          <Collection compact>
            {
              response
                ? <Button onClick={onClose} primary>Close</Button>
                : (
                  <>
                    <Button className="unlink-modal__cancel" secondary disabled={!!isInProgress} onClick={onClose}>Cancel</Button>
                    <Button className="unlink-modal__unlink button--danger secondary" disabled={!!isInProgress} onClick={unlinkHandler}>Unlink</Button>
                  </>
                )
            }
          </Collection>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export const UnlinkModalButton = ({ project, onSuccess }) => {
  const [showModal, setShowModal] = React.useState(false);
  const projectId = _.get(project, 'projectId', null);
  const isSuccessfullyProvisioned = _.get(project, 'project.provisionedState.latest.status') === 'SUCCEEDED';

  if (!project || !projectId) {
    return null;
  }

  return (
    <>
      {
        showModal
        && (
          <UnlinkProjectModal
            projectId={projectId}
            isSuccessfullyProvisioned={isSuccessfullyProvisioned}
            onClose={() => setShowModal(false)}
            onSuccess={onSuccess}
          />
        )
      }
      <Button
        className="button--danger"
        onClick={() => setShowModal(true)}
        disabled={showModal || project.linkedState !== 'LINKED'}
      >
        <i className="fa fa-unlink" />
        {' '}
        Unlink project
      </Button>
    </>
  );
};
